import html2canvas from "html2canvas";

/** Desktop width — keeps xl 2-col / 3-col chart grids like v1 Insights export. */
export const PDF_CAPTURE_VIEWPORT_WIDTH = 1400;

export const PDF_CAPTURE_SCALE = () =>
  Math.min(2, Math.max(1.5, window.devicePixelRatio || 2));

function stashInlineStyle(node, prop, value) {
  if (!node?.style) return null;
  const prev = node.style[prop];
  node.style[prop] = value;
  return [node, prop, prev];
}

export function reflowChartsIn(root, Highcharts) {
  if (!root || !Highcharts?.charts) return;
  Highcharts.charts.forEach((chart) => {
    if (chart?.renderTo && root.contains(chart.renderTo)) {
      try {
        chart.setSize(null, null, false);
      } catch {
        /* ignore */
      }
      chart.reflow();
    }
  });
}

function applyPdfCaptureReadableText(root) {
  if (!root) return;
  const rules = [
    { sel: "h2.text-base.font-semibold", color: "#0f172a" },
    { sel: "h3", color: "#334155" },
    { sel: ".text-slate-900", color: "#0f172a" },
    { sel: ".text-slate-700", color: "#334155" },
    { sel: ".text-slate-500", color: "#64748b" },
    { sel: ".text-slate-400", color: "#64748b" },
  ];
  rules.forEach(({ sel, color }) => {
    root.querySelectorAll(sel).forEach((node) => {
      node.style.color = color;
    });
  });
  root.querySelectorAll(".rounded-2xl.bg-white, .admin-chart-card").forEach((card) => {
    card.style.backgroundColor = "#ffffff";
    card.style.borderColor = "#e2e8f0";
  });
}

function preparePdfSectionForCapture(sectionEl, Highcharts) {
  const restores = [];
  const stash = (node, prop, value) => {
    const entry = stashInlineStyle(node, prop, value);
    if (entry) restores.push(entry);
  };

  stash(sectionEl, "width", `${PDF_CAPTURE_VIEWPORT_WIDTH}px`);
  stash(sectionEl, "minWidth", `${PDF_CAPTURE_VIEWPORT_WIDTH}px`);
  stash(sectionEl, "maxWidth", `${PDF_CAPTURE_VIEWPORT_WIDTH}px`);
  stash(sectionEl, "backgroundColor", "#ffffff");
  applyPdfCaptureReadableText(sectionEl);

  sectionEl.querySelectorAll("[class*='max-h-']").forEach((panel) => {
    stash(panel, "maxHeight", "none");
    stash(panel, "overflow", "visible");
    stash(panel, "overflowY", "visible");
    stash(panel, "height", "auto");
  });

  sectionEl.querySelectorAll(".highcharts-container").forEach((node) => {
    stash(node, "width", "100%");
    stash(node, "height", "auto");
    stash(node, "overflow", "visible");
  });

  reflowChartsIn(sectionEl, Highcharts);
  return restores;
}

function restorePdfSectionCapture(restores) {
  restores.forEach(([node, prop, value]) => {
    if (node?.style) node.style[prop] = value;
  });
}

export async function waitForChartPaint(ms = 200) {
  await new Promise((resolve) => requestAnimationFrame(resolve));
  await new Promise((resolve) => requestAnimationFrame(resolve));
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/** v1-style WYSIWYG — screenshot chart sections exactly as rendered on screen. */
export async function capturePdfChartSection(sectionEl, { scale, Highcharts } = {}) {
  if (!sectionEl) return null;
  const captureScale = scale ?? PDF_CAPTURE_SCALE();
  const restores = preparePdfSectionForCapture(sectionEl, Highcharts);
  await waitForChartPaint(280);
  reflowChartsIn(sectionEl, Highcharts);
  await waitForChartPaint(120);
  try {
    const canvas = await html2canvas(sectionEl, {
      scale: captureScale,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0,
      windowWidth: sectionEl.scrollWidth,
      windowHeight: sectionEl.scrollHeight,
      ignoreElements: (element) => Boolean(element?.closest?.("[data-export-ignore]")),
      onclone: (_doc, cloned) => {
        cloned.querySelectorAll("[data-export-ignore]").forEach((n) => n.remove());
        applyPdfCaptureReadableText(cloned);
        cloned.querySelectorAll("[class*='max-h-']").forEach((panel) => {
          panel.style.maxHeight = "none";
          panel.style.overflow = "visible";
          panel.style.height = "auto";
        });
      },
    });
    return {
      image: canvas.toDataURL("image/png"),
      width: canvas.width,
      height: canvas.height,
      layout: "section",
    };
  } finally {
    restorePdfSectionCapture(restores);
    reflowChartsIn(sectionEl, Highcharts);
  }
}

export async function capturePdfChartSections(sectionEls, options = {}) {
  const sections = sectionEls.filter(Boolean);
  if (!sections.length) return [];
  return Promise.all(sections.map((el) => capturePdfChartSection(el, options)));
}
